import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Signal, Wifi, Smartphone, CreditCard, Router, Headphones, Database, ArrowUpRight,
} from "lucide-react";

const DEVICES = [
    { name: "iPhone", img: "https://images.unsplash.com/photo-1759588071814-1ba7c5761af4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", note: "Latest iPhone lineup" },
    { name: "Samsung Galaxy", img: "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", note: "Galaxy S & A series" },
    { name: "Google Pixel", img: "https://images.unsplash.com/photo-1724322535079-11b08f7f5c88?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", note: "Pixel Pro & standard" },
    { name: "Motorola", img: "https://images.unsplash.com/photo-1707438095940-1eee18e85400?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", note: "Edge & moto g" },
];

const CATEGORIES = [
    { id: "phone-plans", title: "Phone plans", desc: "eSIM wireless with unlimited talk & text and fast data.", icon: Signal, to: "/mobile", live: true, cta: "Shop plans" },
    { id: "esim", title: "eSIM", desc: "Activate in minutes — no physical SIM, no store visit.", icon: Smartphone, to: "/mobile", live: true, cta: "Get an eSIM" },
    { id: "data-only", title: "Data-only plans", desc: "Tablets, hotspots & backup lines — data without a number.", icon: Database, live: false },
    { id: "physical-sim", title: "Physical SIM", desc: "Prefer a classic SIM card? Order one shipped to your door.", icon: CreditCard, live: false },
    { id: "home-internet", title: "Home Internet", desc: "5G home internet with a plug-and-play router.", icon: Wifi, live: false },
    { id: "phones", title: "Phones", desc: "Unlocked iPhone, Samsung, Pixel & Motorola.", icon: Smartphone, live: false },
    { id: "routers", title: "Routers", desc: "5G/LTE routers & hotspots for home and travel.", icon: Router, live: false },
    { id: "accessories", title: "Accessories", desc: "Cases, chargers, cables & more.", icon: Headphones, live: false },
];

export default function Shop() {
    const navigate = useNavigate();
    return (
        <div className="space-y-8" data-testid="shop-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5">
                    <Signal className="w-3.5 h-3.5" /> FlexLine Mobile · Shop
                </p>
                <h1 className="font-display text-4xl font-light tracking-tight">
                    Everything you need to <span className="font-serif-italic">get connected.</span>
                </h1>
                <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
                    Pick a wireless plan and activate an eSIM today. Devices, home internet and accessories are on the way.
                </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-grid">
                {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                        <div key={c.id} data-testid={`shop-cat-${c.id}`}
                            className={`border rounded-sm p-6 flex flex-col ${c.live ? "border-[#DDD6FE] bg-paper" : "border-[#DDD6FE] bg-cream/40"}`}>
                            <div className="flex items-center justify-between">
                                <div className="w-11 h-11 rounded-sm bg-[#4C1D95] grid place-items-center">
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                {!c.live && <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 border border-[#DDD6FE] rounded-sm px-2 py-0.5">Coming soon</span>}
                            </div>
                            <h3 className="font-display text-xl mt-4">{c.title}</h3>
                            <p className="text-sm text-zinc-600 mt-1 flex-1">{c.desc}</p>
                            {c.live ? (
                                <button onClick={() => navigate(c.to)} data-testid={`shop-cta-${c.id}`}
                                    className="mt-4 inline-flex items-center justify-center gap-1.5 bg-espresso text-[#FAF5FF] hover:bg-black rounded-sm h-10 text-sm font-medium">
                                    {c.cta} <ArrowUpRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button disabled data-testid={`shop-cta-${c.id}`}
                                    className="mt-4 inline-flex items-center justify-center bg-zinc-100 text-zinc-400 rounded-sm h-10 text-sm cursor-not-allowed">
                                    Notify me soon
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Phones showcase */}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="shop-phones">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-1">Devices</p>
                        <h2 className="font-display text-3xl font-light">Order a new phone</h2>
                        <p className="text-sm text-zinc-600 mt-1 max-w-xl">
                            Bring your own device today, or shop a brand-new phone that ships with your eSIM pre-configured.
                            Our device store is launching soon.
                        </p>
                    </div>
                    <button
                        onClick={() => toast.info("📱 Phones are launching soon — you'll be able to order right here. We'll notify you!")}
                        data-testid="order-new-phone-btn"
                        className="inline-flex items-center gap-2 bg-espresso text-[#FAF5FF] hover:bg-black rounded-sm h-11 px-6 text-sm font-medium shrink-0"
                    >
                        <Smartphone className="w-4 h-4" /> Order a new phone
                    </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="shop-devices-grid">
                    {DEVICES.map((d) => (
                        <div key={d.name} className="border border-[#DDD6FE] rounded-sm overflow-hidden bg-cream/30" data-testid={`device-${d.name.toLowerCase().replace(/[^a-z]/g, "")}`}>
                            <div className="aspect-[4/3] bg-white overflow-hidden relative">
                                <img src={d.img} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
                                <span className="absolute top-2 right-2 font-mono text-[10px] uppercase tracking-wider bg-black/70 text-white rounded-sm px-2 py-0.5">Coming soon</span>
                            </div>
                            <div className="p-3">
                                <h4 className="font-display text-lg leading-tight">{d.name}</h4>
                                <p className="text-xs text-zinc-500 mt-0.5">{d.note}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
